import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';

import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Device } from 'app/models/device.model';
import { ApiService } from 'app/services/api.service';
import { UntypedFormControl, UntypedFormGroup, Validators, UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HelperService } from 'app/services/helper.service';
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BehaviorSubject } from "rxjs";

@Component({
    selector: 'app-add-device-form',
    imports: [
    FormsModule,
    ReactiveFormsModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    TranslateModule,
    MatProgressSpinnerModule
],
    templateUrl: './add-device-form.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    styleUrls: ['./add-device-form.component.css']
})
export class AddDeviceFormComponent implements OnInit, AfterViewInit {

  constructor(
    private api: ApiService,
    private helper: HelperService,
    private fb: UntypedFormBuilder
  ) { }

  @Output() savedEvent = new EventEmitter<string>();

  @Input() locations: { id: string, name: string }[];
  @Input() errorMsg$: BehaviorSubject<string>;
  @Input() informationMsg$: BehaviorSubject<string>;

  addDevForm: UntypedFormGroup;
  devValidated: boolean = false;
  deviceIsBeingAdded = false;
  newDevice: boolean; // Indicates that device is not found in the system and not belonging to any customer
  responseDev: Device;
  deviceTypeList: { value: string; name: string }[] = [];
  devTypeListStorage: { value: string; name: string }[] = []; // Variable for resetting deviceTypeList to it's original state

  // Device types offered when manually adding a device (basestations, corridor display, door phone).
  // Names are resolved from the cached device type catalog (localStorage "devType").
  // The validated device's own type is appended in updateAddDevForm() if it isn't already present.
  private static readonly SELECTABLE_DEVICE_TYPES = ["31-14", "31-13", "31-15", "31-6", "31-8", "36-1"];

  getDevType(): string {
    return this.helper.getDeviceType(this.responseDev.deviceType);
  }

  /**
   * Check that device exists in the system and it's not already in use
   * If there's no device available and checked id doesn't belong to customer, response returns default values for adding new device
   */
  checkValidity(): void {
    this.api.checkDeviceValidity(this.addDevForm.value.id).subscribe((res) => {
      if (res) {
        this.responseDev = res.device;
        this.newDevice = res.newDevice;
        this.errorMsg$?.next("");
        this.devValidated = true;
        this.updateAddDevForm();

        if (this.newDevice) {
          // translation text needed: (english version) Device not found in the system. New device to be added.
          this.informationMsg$?.next("DEVICE_VALIDITY_NOT_FOUND");
        }
      } else {
        // translation text needed: (english version) Device is already in use.
        this.errorMsg$?.next("DEVICE_VALIDITY_INUSE");
      }
    });
  }

  // Add new device when form is submitted
  addDevice(): void {
    this.devValidated ? this.deviceIsBeingAdded = true : null; // Show loading spinner only after second submit

    // Make sure that device is validated before submitting it to the server
    if (!this.devValidated) {
      this.checkValidity();
      return;
    }

    const dev: Device = new Device();
    dev.id = this.addDevForm.getRawValue().id;
    dev.name = this.addDevForm.value.name;
    dev.locationId = this.addDevForm.value.locationId;
    dev.deviceType = this.addDevForm.value.devType;
    dev.devClass = this.addDevForm.value.devType.substring(3);

    this.api.addDevice(dev).subscribe(
      () => {
        this.savedEvent.emit(dev.id);
        this.deviceIsBeingAdded = false;
        if (JSON.parse(localStorage.getItem("features")).includes("DeviceRegistry")) {
          this.api.addDeviceToInventory(dev).subscribe(() => {
          }, (err) => {
            console.log(err);
          });
        }
      },
      (err) => {
        this.errorMsg$?.next("ERROR");
        this.deviceIsBeingAdded = false;
        console.log(err);
      }
    );
  }

  // Initialize the form for adding new devices
  createAddDevForm(): void {
    this.addDevForm = this.fb.group({
      id: [
        "",
        Validators.compose([
          Validators.required,
          Validators.pattern(
            new RegExp("^(?:31|34|36|06|89)[0-9A-F]{6}$", "i")
          ),
        ]),
      ],
    });
  }

  // Reset the form to it's initial state
  resetAddForm(): void {
    this.errorMsg$?.next("");
    this.informationMsg$?.next("");
    this.addDevForm.reset({ id: "" }); // Make form reset and set id back to empty
    this.addDevForm.controls["id"].enable(); // Enable the id field and remove extra fields which are not needed before validation
    this.addDevForm.removeControl("locationId");
    this.addDevForm.removeControl("name");
    this.addDevForm.removeControl("devType");
    this.devValidated = false;
    this.deviceTypeList = [...this.devTypeListStorage];
  }

  // Update the form after successfull validity check
  updateAddDevForm(): void {
    // Disable the id field so user can't change it after the validity check
    this.addDevForm.controls["id"].disable();

    // Add new fields to the form
    this.addDevForm.addControl("locationId", new UntypedFormControl("", Validators.required));
    this.addDevForm.addControl("name", new UntypedFormControl(""));
    this.addDevForm.addControl("devType", new UntypedFormControl(this.newDevice ? "" : this.responseDev.deviceType, Validators.required));

    // Remove required validator if device is not new
    if (!this.newDevice) {
      this.addDevForm.get("devType").setValidators(null);
      this.addDevForm.get("devType").updateValueAndValidity();
    }

    // Add response device's type to the list if doesn't already exists there
    if (!this.deviceTypeList.some((e: any) => e.value === this.responseDev.deviceType)) {
      this.deviceTypeList.push({
        value: this.responseDev.deviceType,
        name: this.helper.getDeviceType(this.responseDev.deviceType),
      });
    }

    //Remove device type 34-4 from the deviceTypeList since that is Nightowl camera and they are added separately
    this.deviceTypeList = this.deviceTypeList.filter(
      (devType: any) => devType.value !== "34-4"
    );

    // Filter device type selection to match the given serial number ID for correct device class selection
    this.deviceTypeList = this.deviceTypeList.filter(
      (devType: any) => devType.value.substring(0, 2) === this.responseDev.deviceType.substring(0, 2)
    );
  }

  ngOnInit() {
    this.createAddDevForm();

    // Build the device type list from the curated set of manually-addable types.
    // Names are resolved from the cached device type catalog (localStorage "devType").
    // The list may be incomplete; the validated device's type is appended in updateAddDevForm().
    this.deviceTypeList = AddDeviceFormComponent.SELECTABLE_DEVICE_TYPES.map((value) => ({
      value,
      name: this.helper.getDeviceType(value),
    }));
    this.devTypeListStorage = [...this.deviceTypeList];
  }

  ngAfterViewInit() {
    // Use timeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.errorMsg$?.next("");
      this.informationMsg$?.next("");
    });
  }
}
